import React, { useEffect, useMemo, useState } from 'react';
import {
  BrandingDraft,
  COLOR_FIELDS,
  EMPTY_DRAFT,
  ValidationIssue,
  brandingAdminService,
  toDraft,
} from '../../../../services/brandingAdminService';
import { ThemeColors, cleanColors, themeValues } from '../../../../theme/engine';
import { contrastRatio, readableOn, toChannels } from '../../../../theme/color';
import { FONT_OPTIONS, fontOption, fontStack, fontStylesheet } from '../../../../theme/fonts';
import { PRODUCT } from '../../../../theme/product';
import { fetchImageUrl } from '../../../../helpers/helper';
import { Button } from '../../../../components/ui/Button';
import { SelectField, TextField } from '../../../../components/ui/Field';
import { Surface } from '../../../../components/ui/Surface';

interface BrandingTabProps {
  tenantId: string;
}

const HEX = /^#[0-9a-f]{6}$/i;

/**
 * A client's branding: identity, links, colours and font.
 *
 * Kept short on purpose. Only colours that cannot be worked out from others are
 * asked for; hover shades, card and list backgrounds, borders and text on
 * buttons are calculated. Logo and favicon come from the Settings tab. Any
 * field left empty uses the product's value.
 */
const BrandingTab: React.FC<BrandingTabProps> = ({ tenantId }) => {
  const [draft, setDraft] = useState<BrandingDraft>(EMPTY_DRAFT);
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [issues, setIssues] = useState<ValidationIssue[]>([]);
  const [status, setStatus] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    setLoading(true);
    brandingAdminService
      .get(tenantId)
      .then(async (res: any) => {
        if (cancelled) return;
        setDraft(toDraft(res?.branding));
        const url = res?.logo ? await fetchImageUrl(res.logo).catch(() => null) : null;
        if (!cancelled) setLogoUrl(url ?? res?.fallbackLogoUrl ?? PRODUCT.logoUrl);
      })
      .catch(() => {
        if (!cancelled) setStatus('Could not load branding.');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [tenantId]);

  const set = <K extends keyof BrandingDraft>(key: K, value: BrandingDraft[K]) => {
    setDraft((d) => ({ ...d, [key]: value }));
    setStatus(null);
  };

  const setColor = (key: keyof ThemeColors, value: string) => {
    setDraft((d) => {
      const colors = { ...d.colors };
      // Empty removes the colour, so the product's applies again.
      if (value.trim() === '') delete colors[key];
      else colors[key] = value.trim();
      return { ...d, colors };
    });
    setStatus(null);
  };

  // Warn before saving; the server applies the same rule.
  const contrastWarning = useMemo(() => {
    const text = draft.colors.text;
    const background = draft.colors.background;
    if (!text || !background || !HEX.test(text) || !HEX.test(background)) return null;
    const ratio = contrastRatio(text, background);
    if (ratio >= 4.5) return null;
    return `Text on the page background is ${ratio.toFixed(2)}:1. At least 4.5:1 is needed, so this will be rejected on save.`;
  }, [draft.colors]);

  const save = async () => {
    setSaving(true);
    setIssues([]);
    setStatus(null);

    try {
      await brandingAdminService.update(tenantId, draft);
      setStatus("Branding saved. The client's users see it on their next page load.");
    } catch (err: any) {
      // apiClient attaches the parsed error body as `details`.
      const body = err?.details;
      if (body?.issues?.length) setIssues(body.issues);
      else setStatus('Could not save branding.');
    } finally {
      setSaving(false);
    }
  };

  const issueFor = (field: string) => issues.find((i) => i.field === field)?.message;

  if (loading) return <div className="text-agentic-text-secondary">Loading branding…</div>;

  const field = (key: keyof BrandingDraft, label: string, placeholder: string, hint?: string) => (
    <div className="flex flex-col gap-1.5">
      <label className="text-sm font-medium text-gray-700">{label}</label>
      <TextField
        type="text"
        value={String(draft[key] ?? '')}
        onChange={(e) => set(key, e.target.value as any)}
        placeholder={placeholder}
        className="rounded-lg border px-3 py-2 focus:ring-2 focus:ring-agentic-primary-blue"
      />
      {issueFor(key) ? (
        <p className="text-xs text-red-600">{issueFor(key)}</p>
      ) : (
        hint && <p className="text-xs text-gray-500">{hint}</p>
      )}
    </div>
  );

  return (
    <div className="grid gap-8 xl:grid-cols-[minmax(0,1fr)_360px]">
      <div className="space-y-8">
        <div>
          <h2 className="text-xl font-semibold">Branding</h2>
          <p className="mt-1 text-sm text-agentic-text-secondary">
            How this client's users see the product. Anything left empty uses the product's own
            value. The logo and favicon are set on the Settings tab.
          </p>
        </div>

        <section className="rounded-xl border bg-gray-50 p-6">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Identity</h3>
          <div className="grid gap-4 md:grid-cols-2">
            {field('productName', 'Product name', 'Ask Client', 'Shown in the header, page title and welcome text.')}
            {field('legalName', 'Legal name', 'Client College Inc.', 'Used for the copyright notice.')}
            {field('tagline', 'Tagline', 'Answers for applicants, day or night.')}
            {field('copyrightText', 'Copyright notice', '© 2026 Client College. All rights reserved.', 'Leave empty to build one from the legal name.')}
            {field('supportEmail', 'Support email', 'help@client.edu')}
          </div>
        </section>

        <Surface tone="plain" as="section" className="rounded-xl border p-6">
          <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500">Links</h3>
          <p className="mb-4 text-xs text-gray-500">Shown in the footer. Empty links use the product's.</p>
          <div className="grid gap-4 md:grid-cols-3">
            {field('termsUrl', 'Terms of service', 'https://client.edu/terms')}
            {field('privacyUrl', 'Privacy policy', 'https://client.edu/privacy')}
            {field('supportUrl', 'Support page', 'https://client.edu/support')}
          </div>
        </Surface>

        <Surface tone="plain" as="section" className="rounded-xl border p-6">
          <h3 className="mb-1 text-sm font-semibold uppercase tracking-wide text-gray-500">Colours</h3>
          <p className="mb-4 text-xs text-gray-500">
            Every other colour in the app (hover shades, card, list and tab backgrounds, borders,
            text on buttons) is worked out from these. Leave one empty to keep the product's.
          </p>

          <div className="grid gap-4 md:grid-cols-2">
            {COLOR_FIELDS.map((c) => {
              const value = draft.colors[c.key] ?? '';
              const err = issueFor(`colors.${c.key}`);
              return (
                <div key={c.key} className="flex flex-col gap-1.5">
                  <label className="text-sm font-medium text-gray-700">{c.label}</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="color"
                      aria-label={`${c.label} colour picker`}
                      value={HEX.test(value) ? value : '#ffffff'}
                      onChange={(e) => setColor(c.key, e.target.value)}
                      className="h-9 w-10 cursor-pointer rounded border"
                    />
                    <TextField
                      type="text"
                      value={value}
                      onChange={(e) => setColor(c.key, e.target.value)}
                      placeholder="product default"
                      className="w-full rounded-lg border px-3 py-2 font-mono text-sm"
                    />
                    {value && (
                      <Button tone="custom" type="button" onClick={() => setColor(c.key, '')} className="text-xs text-gray-500 hover:text-gray-700">
                        Clear
                      </Button>
                    )}
                  </div>
                  {err ? <p className="text-xs text-red-600">{err}</p> : <p className="text-xs text-gray-500">{c.hint}</p>}
                </div>
              );
            })}
          </div>

          {contrastWarning && (
            <p className="mt-4 rounded-lg bg-amber-50 px-3 py-2 text-xs text-amber-800">{contrastWarning}</p>
          )}
        </Surface>

        <Surface tone="plain" as="section" className="rounded-xl border p-6">
          <h3 className="mb-4 text-sm font-semibold uppercase tracking-wide text-gray-500">Font</h3>
          <SelectField
            value={draft.fontFamily}
            onChange={(e) => set('fontFamily', e.target.value)}
            className="w-full rounded-lg border px-3 py-2 md:w-1/2"
          >
            <option value="">Product default</option>
            {FONT_OPTIONS.map((f) => (
              <option key={f.name} value={f.name}>
                {f.name}
              </option>
            ))}
          </SelectField>
        </Surface>

        {issues.length > 0 && (
          <div className="rounded-lg bg-red-50 px-4 py-3 text-sm text-red-700">
            <p className="font-medium">This couldn't be saved:</p>
            <ul className="mt-1 list-disc pl-5">
              {issues.map((i) => (
                <li key={i.field}>{i.message}</li>
              ))}
            </ul>
          </div>
        )}

        {status && <p className="text-sm text-agentic-text-secondary">{status}</p>}

        <div className="flex justify-end">
          <Button tone="custom"
            onClick={save}
            disabled={saving}
            className="rounded-lg bg-agentic-primary-blue px-6 py-2 text-on-primary transition hover:opacity-90 disabled:opacity-50"
          >
            {saving ? 'Saving…' : 'Save branding'}
          </Button>
        </div>
      </div>

      <aside className="xl:sticky xl:top-4 xl:self-start">
        <BrandingPreview draft={draft} logoUrl={logoUrl} />
      </aside>
    </div>
  );
};

/**
 * A small sample page drawn with the unsaved draft through the real theme
 * engine: the calculated colours are set as CSS variables on this box only, so
 * it shows exactly what the app will look like without changing the page around it.
 */
const BrandingPreview: React.FC<{ draft: BrandingDraft; logoUrl: string | null }> = ({ draft, logoUrl }) => {
  const colors = useMemo(() => cleanColors({ ...PRODUCT.colors, ...draft.colors } as Record<string, unknown>), [draft.colors]);

  const vars = useMemo(() => {
    const style: Record<string, string> = {};
    for (const [name, hex] of Object.entries(themeValues(colors))) style[name] = toChannels(hex);
    if (colors.primary) style['--theme-on-primary'] = toChannels(readableOn(colors.primary, '#111111', '#ffffff'));
    return style as React.CSSProperties;
  }, [colors]);

  const font = fontOption(draft.fontFamily || PRODUCT.fontFamily);
  const stylesheet = font ? fontStylesheet(font) : null;
  const header = colors.header;
  const footer = colors.footer;
  const name = draft.productName || PRODUCT.name || 'Product';
  const tabs = ['Overview', 'Settings', 'Activity'];

  return (
    <div className="space-y-2">
      <h3 className="text-sm font-semibold uppercase tracking-wide text-gray-500">Preview</h3>
      {stylesheet && <link rel="stylesheet" href={stylesheet} />}

      <div
        className="overflow-hidden rounded-xl border bg-agentic-white text-agentic-text-primary shadow-sm"
        style={{ ...vars, fontFamily: font ? fontStack(font.name) : undefined }}
      >
        <div
          className="flex items-center gap-2 bg-c-e7dff4/40 px-4 py-3"
          style={header ? { background: header, color: readableOn(header, colors.text ?? '#111827', '#ffffff') } : undefined}
        >
          {logoUrl && <img src={logoUrl} alt="" className="h-6 max-w-[96px] object-contain" />}
          <span className="text-sm font-semibold">{name}</span>
          <span className="ml-auto rounded-xl bg-gradient-to-r from-kv-agentic-purple to-kv-agentic-blue px-3 py-1 text-xs text-on-primary">
            Home
          </span>
        </div>

        <div className="space-y-3 bg-gray-50 p-4">
          <div className="flex gap-4 border-b border-gray-200 text-xs">
            {tabs.map((t, i) => (
              <span
                key={t}
                className={`-mb-px border-b-2 pb-2 ${i === 0 ? 'border-agentic-primary-blue font-semibold text-agentic-primary-blue' : 'border-transparent text-gray-500'}`}
              >
                {t}
              </span>
            ))}
          </div>

          <div className="space-y-2 rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-sm font-semibold text-gray-900">{draft.tagline || 'Card title'}</p>
            <p className="text-xs text-gray-500">Secondary text, borders and backgrounds are worked out from your colours.</p>
            <div className="rounded-md bg-blue-50 px-2 py-1 text-xs text-blue-700">Highlighted row</div>
            <div className="flex gap-2 pt-1">
              <span className="rounded-lg bg-agentic-primary-blue px-3 py-1.5 text-xs font-medium text-on-primary">Primary</span>
              <span className="rounded-lg border border-agentic-primary-blue px-3 py-1.5 text-xs font-medium text-agentic-primary-blue">Outline</span>
              <span className="rounded-lg bg-gradient-to-r from-kv-agentic-purple to-kv-agentic-blue px-3 py-1.5 text-xs font-medium text-on-primary">
                Gradient
              </span>
            </div>
          </div>
        </div>

        <div
          className="bg-c-d7cff6 px-4 py-2 text-[11px] text-c-3f3a63"
          style={footer ? { background: footer, color: readableOn(footer, colors.text ?? '#111827', '#ffffff') } : undefined}
        >
          {draft.copyrightText || `© ${new Date().getFullYear()} ${draft.legalName || name}`}
          {PRODUCT.poweredBy && <span className="ml-2 opacity-70">Powered by {PRODUCT.poweredBy}</span>}
        </div>
      </div>
    </div>
  );
};

export default BrandingTab;
